import React, { useState } from 'react';
import { useParams } from 'wouter';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { 
  Database, 
  BarChart3, 
  Activity, 
  Eye,
  Clock,
  Zap
} from 'lucide-react';
import { DMVDashboard } from '@/components/dmv-dashboard';

interface ObservePageParams {
  clientCode: string;
}

export default function ObservePage() {
  const { clientCode } = useParams<ObservePageParams>();
  const [activeTab, setActiveTab] = useState('dmvs');

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="text-center space-y-4">
        <h1 className="text-4xl font-bold text-gray-900 flex items-center justify-center gap-3">
          <Eye className="w-10 h-10 text-shiftleft-600" />
          SQL Server Observability Dashboard
        </h1>
        <p className="text-lg text-gray-600 max-w-3xl mx-auto">
          Comprehensive monitoring and observability for SQL Server environments.
          Monitor performance, analyze queries, and track system events in real-time.
        </p>
        
        {/* Client Badge */}
        <div className="flex justify-center">
          <Badge variant="outline" className="text-lg px-4 py-2">
            Client: {clientCode}
          </Badge>
        </div>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 mb-6">
          <TabsTrigger value="dmvs" className="flex items-center gap-2">
            <Database className="w-4 h-4" />
            Dynamic Management Views
          </TabsTrigger>
          <TabsTrigger value="query-store" className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Query Store
          </TabsTrigger>
          <TabsTrigger value="extended-events" className="flex items-center gap-2">
            <Activity className="w-4 h-4" />
            Extended Events
          </TabsTrigger>
        </TabsList>

        {/* DMVs Tab */}
        <TabsContent value="dmvs" className="space-y-6">
          <DMVDashboard />
        </TabsContent>

        {/* Query Store Tab */}
        <TabsContent value="query-store" className="space-y-6">
          <QueryStoreDashboard clientCode={clientCode} />
        </TabsContent>

        {/* Extended Events Tab */}
        <TabsContent value="extended-events" className="space-y-6">
          <ExtendedEventsDashboard clientCode={clientCode} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Query Store Dashboard Component
function QueryStoreDashboard({ clientCode }: { clientCode: string }) {
  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            <BarChart3 className="w-6 h-6 text-green-600" />
            Query Store Dashboard
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-600 mb-6">
            Monitor query performance, track regression, and analyze execution statistics using SQL Server Query Store.
          </p>
          
          {/* Query Store Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Top Resource Consuming Queries */}
            <Card className="bg-green-50 border-green-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2 text-green-700">
                  <Zap className="w-5 h-5" />
                  Top Resource Queries
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-3">
                  Identify queries consuming the most CPU, memory, and I/O resources.
                </p>
                <div className="space-y-2">
                  <div className="text-xs text-gray-500">Key Metrics:</div>
                  <div className="flex flex-wrap gap-1">
                    <Badge variant="secondary" className="text-xs">CPU Time</Badge>
                    <Badge variant="secondary" className="text-xs">Logical Reads</Badge>
                    <Badge variant="secondary" className="text-xs">Duration</Badge>
                    <Badge variant="secondary" className="text-xs">Execution Count</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Regressed Queries */}
            <Card className="bg-red-50 border-red-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2 text-red-700">
                  <Activity className="w-5 h-5" />
                  Regressed Queries
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-3">
                  Track queries that have experienced performance regression over time.
                </p>
                <div className="space-y-2">
                  <div className="text-xs text-gray-500">Detection Methods:</div>
                  <div className="flex flex-wrap gap-1">
                    <Badge variant="secondary" className="text-xs">Plan Changes</Badge>
                    <Badge variant="secondary" className="text-xs">Duration Increase</Badge>
                    <Badge variant="secondary" className="text-xs">Resource Spike</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Query Wait Statistics */}
            <Card className="bg-orange-50 border-orange-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2 text-orange-700">
                  <Clock className="w-5 h-5" />
                  Query Wait Stats
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-3">
                  Analyze wait statistics at the query level to identify bottlenecks.
                </p>
                <div className="space-y-2">
                  <div className="text-xs text-gray-500">Wait Categories:</div>
                  <div className="flex flex-wrap gap-1">
                    <Badge variant="secondary" className="text-xs">CPU</Badge>
                    <Badge variant="secondary" className="text-xs">I/O</Badge>
                    <Badge variant="secondary" className="text-xs">Locking</Badge>
                    <Badge variant="secondary" className="text-xs">Memory</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>

      {/* Placeholder for actual Query Store implementation */}
      <Card>
        <CardContent className="pt-6">
          <div className="text-center py-12 text-gray-500">
            <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium">Query Store Dashboard</p>
            <p className="text-sm mt-2">Full Query Store implementation coming soon</p>
            <p className="text-xs mt-1">Will include real-time query performance monitoring and plan management</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Extended Events Dashboard Component
function ExtendedEventsDashboard({ clientCode }: { clientCode: string }) {
  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            <Activity className="w-6 h-6 text-purple-600" />
            Extended Events Dashboard
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-600 mb-6">
            Monitor SQL Server Extended Events sessions, capture system activity, and analyze performance events in real-time.
          </p>
          
          {/* Extended Events Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Active Sessions */}
            <Card className="bg-purple-50 border-purple-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2 text-purple-700">
                  <Activity className="w-5 h-5" />
                  Active Sessions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-3">
                  Monitor currently running Extended Events sessions and their status.
                </p>
                <div className="space-y-2">
                  <div className="text-xs text-gray-500">Session Types:</div>
                  <div className="flex flex-wrap gap-1">
                    <Badge variant="secondary" className="text-xs">Performance</Badge>
                    <Badge variant="secondary" className="text-xs">Security</Badge>
                    <Badge variant="secondary" className="text-xs">Deadlocks</Badge>
                    <Badge variant="secondary" className="text-xs">Blocking</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Event Capture */}
            <Card className="bg-green-50 border-green-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2 text-green-700">
                  <Database className="w-5 h-5" />
                  Event Capture
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-3">
                  Configure and manage event capture for different SQL Server activities.
                </p>
                <div className="space-y-2">
                  <div className="text-xs text-gray-500">Event Categories:</div>
                  <div className="flex flex-wrap gap-1">
                    <Badge variant="secondary" className="text-xs">SQL Statements</Badge>
                    <Badge variant="secondary" className="text-xs">Logins</Badge>
                    <Badge variant="secondary" className="text-xs">Errors</Badge>
                    <Badge variant="secondary" className="text-xs">Waits</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Deadlock Analysis */}
            <Card className="bg-red-50 border-red-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2 text-red-700">
                  <Zap className="w-5 h-5" />
                  Deadlock Analysis
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-3">
                  Capture and analyze deadlock events with detailed deadlock graphs.
                </p>
                <div className="space-y-2">
                  <div className="text-xs text-gray-500">Analysis Features:</div>
                  <div className="flex flex-wrap gap-1">
                    <Badge variant="secondary" className="text-xs">Deadlock Graph</Badge>
                    <Badge variant="secondary" className="text-xs">Victim Selection</Badge>
                    <Badge variant="secondary" className="text-xs">Resource Chain</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>

      {/* Placeholder for actual Extended Events implementation */}
      <Card>
        <CardContent className="pt-6">
          <div className="text-center py-12 text-gray-500">
            <Activity className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium">Extended Events Dashboard</p>
            <p className="text-sm mt-2">Full Extended Events implementation coming soon</p>
            <p className="text-xs mt-1">Will include real-time event monitoring and session management</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}